import React, { Fragment, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate, useLocation } from 'react-router-dom'
import { RootState, AppDispatch } from '../../app/store'
import { Menu as HeadlessMenu, Transition } from '@headlessui/react'
import { ChevronDown, Check, X, Menu, Lock, Loader2 } from 'lucide-react'
import { setLanguage } from '../../features/settings/settingsSlice'
import fallbackLogo from "../../assets/images/logo_en.svg";
import userIcon from "../../assets/images/user.svg";
import arFlag from "../../assets/images/AR-b.png";
import usFlag from "../../assets/images/US.png";
import { getCurrentDesign } from '@/services/settingsService'
import SideNav from '../navigation/SideNav'
import apiService from '../../services/api'
import { logout } from '../../features/auth/authSlice'
import toast from 'react-hot-toast'
import { scheduleFullRefresh } from '@/utils/sessionHelpers'

interface TopBarProps {
  userName?: string
  onCloseShift?: () => void
}

const TopBar: React.FC<TopBarProps> = ({
  userName
}) => {
  const { t, i18n } = useTranslation()
  const dispatch = useDispatch<AppDispatch>()
  const navigate = useNavigate()
  const { settings } = useSelector((state: RootState) => state.settings)
  const { user } = useSelector((state: RootState) => state.auth)
  
  const isRTL = settings.language === 'ar'
  const currency = useSelector((state: RootState) => state.settings.settings.currency || '')
  const [isSideNavOpen, setIsSideNavOpen] = useState(false)
  const [showConfirmCloseModal, setShowConfirmCloseModal] = useState(false)
  const [showClosingBalanceModal, setShowClosingBalanceModal] = useState(false)
  const [closingAmount, setClosingAmount] = useState('')
  const [closingError, setClosingError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  
  // Get user name from prop or Redux state, check multiple possible field names
  // API structure: data.employee.user.name
  const displayName = userName || 
    user?.name || 
    (user as any)?.user?.name ||
    (user as any)?.employee_name || 
    (user as any)?.full_name || 
    ''

  // Logo from API design settings
  const [headerLogoUrl, setHeaderLogoUrl] = useState<string | undefined>(() => getCurrentDesign().headerLogo)

  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail as { headerLogo?: string } | undefined
      if (detail && detail.headerLogo) {
        setHeaderLogoUrl(detail.headerLogo)
      }
    }
    window.addEventListener('designSettingsApplied', handler as (event: Event) => void)
    return () => window.removeEventListener('designSettingsApplied', handler as (event: Event) => void)
  }, [])

  
  // Language options
  const languages = [
    { code: 'ar', name: 'العربية', flag: arFlag },
    { code: 'en', name: 'English', flag: usFlag },
  ]
  
  // Get current language flag
  const currentLanguage = languages.find(lang => lang.code === settings.language)
  const currentFlag = currentLanguage?.flag || arFlag
  
  // Handle language change
  const handleLanguageChange = async (languageCode: string) => {
    try {
      // Update i18n immediately
      await i18n.changeLanguage(languageCode)
      
      // Update Redux state immediately (this will trigger header re-render)
      dispatch(setLanguage(languageCode as 'ar' | 'en'))
      
      // Update localStorage immediately for API headers
      localStorage.setItem('language', languageCode)
      
      // Trigger API refresh by dispatching a custom event
      // App.tsx will listen to this and refresh all APIs
      window.dispatchEvent(new CustomEvent('languageChanged', { detail: { language: languageCode } }))
      
      // Also refresh API configuration immediately
      apiService.refreshConfiguration()
    } catch (error) {
    }
  }


  const handleCloseSessionClick = () => {
    setShowConfirmCloseModal(true)
  }

  const handleLogoClick = () => {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('homeLogoClicked'))
      setTimeout(() => {
        window.location.reload()
      }, 10)
    }
    navigate('/')
  }

  const handleConfirmClose = () => {
    setShowConfirmCloseModal(false)
    setClosingAmount('')
    setClosingError('')
    setShowClosingBalanceModal(true)
  }

  const handleCloseModals = () => {
    if (isSubmitting) return
    setShowConfirmCloseModal(false)
    setShowClosingBalanceModal(false)
    setClosingAmount('')
    setClosingError('')
  }

  const handleCloseShift = async () => {
    const sanitizedValue = closingAmount.trim().replace(',', '.')
    const parsedAmount = parseFloat(sanitizedValue)

    if (!sanitizedValue) {
      setClosingError(t('shift.validation.amount_required'))
      return
    }

    if (Number.isNaN(parsedAmount) || parsedAmount < 0) {
      setClosingError(t('shift.validation.amount_invalid'))
      return
    }

    setIsSubmitting(true)
    setClosingError('')

    try {
      await apiService.post('/cashier/shifts/close', {
        entered_closing_balance: parsedAmount
      })

      await apiService.post('/cashier/auth/logout')

      setShowClosingBalanceModal(false)
      setShowConfirmCloseModal(false)
      setClosingAmount('')

      dispatch(logout())
      toast.success(t('auth.logout_success'))
      navigate('/login', { replace: true })
      scheduleFullRefresh()
    } catch (error: any) {
      const message =
        error?.response?.data?.message ??
        (error?.response?.status === 422
          ? t('shift.validation.amount_invalid')
          : t('shift.error.close_failed'))

      setClosingError(message)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <>
    <div className={` 
    mt-0    pb-4  flex items-center justify-between gap-3 shrink-0 ${isRTL ? 'pr-4 pl-0' : 'pl-4 pr-0'}`} >
    <div className={`flex items-center gap-2 flex-row-reverse`}>
     

      {/* Logo */}
      <img 
      onClick={handleLogoClick}
        src={headerLogoUrl || fallbackLogo} 
        alt="brand logo" 
        className="h-[60px] w-auto object-contain" 
      /> 
       {/* Menu Button - Next to Logo */}
       <div className="relative">
        <div className="size-12 p-1  rounded-[80px] inline-flex justify-end items-center gap-2 overflow-hidden">
          <button
            onClick={() => setIsSideNavOpen(true)}
            className="flex-1 self-stretch px-4.5 bg-white rounded-[12px] shadow-[inset_0px_0px_0px_1px_rgba(0,0,0,0.25)] flex justify-center items-center gap-4.5 overflow-hidden"
          >
            <Menu className="size-6 text-flutter-button" />
          </button>
        </div>
      </div>
    </div>
    
<div className='flex flex-row items-center '>

  {/* User Info */}
       {/* Language Switcher */}
      <HeadlessMenu as="div" className="relative ml-2 mr-2  ">
        <HeadlessMenu.Button className={`
          flex items-center gap-0.5
          ${isRTL ? 'flex-row-reverse' : ''}
        `}>
          <img src={currentFlag} alt={settings.language} className="w-5 h-5 object-contain" />
          <ChevronDown className="w-5 h-5 text-gray-600 " />
        </HeadlessMenu.Button>

        <Transition
          as={Fragment}
          enter="transition ease-out duration-100"
          enterFrom="transform opacity-0 scale-95"
          enterTo="transform opacity-100 scale-100"
          leave="transition ease-in duration-75"
          leaveFrom="transform opacity-100 scale-100"
          leaveTo="transform opacity-0 scale-95"
        >
          <HeadlessMenu.Items className={`
            absolute top-full mt-2 w-32 bg-white rounded-lg shadow-lg ring-1 ring-black ring-opacity-5
            z-50
            ${isRTL ? 'left-0' : 'right-0'}
          `}>
            <div className="py-1">
              {languages.map((lang) => (
                <HeadlessMenu.Item key={lang.code}>
                  {({ active }) => (
                    <button
                      onClick={() => handleLanguageChange(lang.code)}
                      className={`
                        flex items-center gap-3 w-full px-4 py-2 text-sm transition-colors flex-row-reverse
                        ${active ? 'bg-gray-100 text-gray-900' : 'text-gray-700'}
                      `}
                    >
                      <img src={lang.flag} alt={lang.code} className="w-5 h-5 object-contain" />
                      <span className="font-medium">{lang.name}</span>
                      {settings.language === lang.code && (
                        <Check className="w-4 h-4 text-[var(--brand-primary)]" />
                      )}
                    </button>
                  )}
                </HeadlessMenu.Item>
              ))}
            </div>
          </HeadlessMenu.Items>
        </Transition>
      </HeadlessMenu>



      <div onClick={() => navigate('/profile')}>

<div  className={`h-10 pl-3.5 pr-1 py-2.5 ml-15 mr-15 bg-white rounded-[40px] flex items-center gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
        <span className={`text-sm font-medium font-['IBM Plex Sans Arabic'] text-black ${isRTL ? 'text-right' : 'text-left'}`}>
          {displayName}
        </span>
        <div className="w-8 h-8 rounded-[32px] bg-neutral-100 flex items-center justify-center">
          <img src={userIcon} alt="user" className="w-8 h-8" />
        </div>
      </div>

    </div>
      


</div>
     

    
       
    </div>
    
    {/* Side Navigation */}
    <SideNav 
      isOpen={isSideNavOpen}
      onClose={() => setIsSideNavOpen(false)}
      onCloseShift={handleCloseSessionClick}
    />

    {/* Confirm Close Modal */}
    {showConfirmCloseModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="absolute inset-0 bg-black/20 backdrop-blur-sm"
          onClick={handleCloseModals}
        />
        <div className="relative w-full max-w-md rounded-3xl bg-white shadow-[0px_24px_48px_rgba(18,18,23,0.08)] p-8 space-y-6">
          <div className="flex flex-col items-center gap-2">
            <h3 className="text-2xl font-medium text-neutral-900 text-center font-['IBM Plex Sans Arabic']">
              {t('shift.confirm_logout_title')}
            </h3>
            <p className="text-base text-black text-center leading-6 font-['IBM Plex Sans Arabic']">
              {t('shift.confirm_logout_subtitle')}
            </p>
          </div>
          <div className="flex flex-col gap-3">
            <button
              onClick={handleConfirmClose}
              className="w-full h-12 rounded-xl bg-flutter-button text-white font-semibold flex items-center justify-center gap-2 shadow-[inset_0_0_0_1px_rgba(0,0,0,0.15)]"
            >
              <Lock className="h-4 w-4" />
              <span className="font-['IBM Plex Sans Arabic']">
                {t('shift.confirm_logout_action')}
              </span>
            </button>
            <button
              onClick={handleCloseModals}
              className="h-12 rounded-xl border border-black bg-white text-black font-semibold flex items-center justify-center font-['IBM Plex Sans Arabic']"
            >
              {t('common.cancel')}
            </button>
          </div>
        </div>
      </div>
    )}

    {/* Closing Balance Modal */}
    {showClosingBalanceModal && (
      <div className="fixed inset-0 z-50 flex items-center justify-center">
        <div
          className="absolute inset-0 bg-black/20 backdrop-blur-sm"
          onClick={handleCloseModals}
        />
        <div className="relative w-full max-w-md rounded-3xl bg-white shadow-[0px_24px_48px_rgba(18,18,23,0.08)] p-8 space-y-6">
          <button
            type="button"
            onClick={handleCloseModals}
            className={`absolute top-4 ${isRTL ? 'left-4' : 'right-4'} w-10 h-10 rounded-xl bg-white border border-gray-200 flex items-center justify-center hover:bg-neutral-200 transition`}
          >
            <X className="h-5 w-5 text-black" />
          </button>

          <div className="flex flex-col items-center gap-2 pt-2">
            <h3 className="text-2xl font-medium text-neutral-900 text-center font-['IBM Plex Sans Arabic']">
              {t('shift.enter_closing_amount_title')}
            </h3>
            <p className="text-base text-black text-center leading-6 font-['IBM Plex Sans Arabic']">
              {t('shift.enter_closing_amount_subtitle')}
            </p>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-neutral-600 font-['IBM Plex Sans Arabic']">
              {t('shift.entered_balance_label')}
            </label>
            <div className="relative">
              <input
                value={closingAmount}
                onChange={(event) => {
                  const sanitized = event.target.value.replace(/[^\d.,]/g, '').replace(',', '.')
                  setClosingAmount(sanitized)
                  setClosingError('')
                }}
                inputMode="decimal"
                dir="ltr"
                placeholder={t('shift.entered_balance_placeholder')}
                disabled={isSubmitting}
                className="w-full h-12 rounded-xl px-4 pr-16 text-base font-semibold text-neutral-900 focus:border-none focus:outline-none focus:ring-2 "
              />
              {currency && (
                <span className="absolute inset-y-0 right-4 flex items-center text-sm font-medium text-neutral-400">
                  {currency}
                </span>
              )}
            </div>
            {closingError && (
              <p className="text-sm text-red-500 font-['IBM Plex Sans Arabic']">{closingError}</p>
            )}
          </div>

          <button
            onClick={handleCloseShift}
            disabled={isSubmitting}
            className="w-full h-12 rounded-xl bg-flutter-button text-white font-semibold flex items-center justify-center gap-2 disabled:opacity-70 font-['IBM Plex Sans Arabic']"
          >
            {isSubmitting && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('common.confirm')}
          </button>
        </div>
      </div>
    )}
    </>
  )
}

export default TopBar

